// Version: 1.0
// 1.0: Pipeline rules (CV-54, app.py v4.37). Opened from the Pipeline's Rules
//      button (sales.rules permission). Lists each rule as When, If and Then
//      with an on/off switch, how often it has fired and any problem (a tag it
//      uses was deleted). The builder sets the trigger (stage change, tag
//      added or removed, tag on a lead for N days, no response for N days,
//      next action overdue, prospect responds, email opened, lead added),
//      conditions that must all be true, and actions (move stage, revive, add
//      or remove tags, remove the triggering tag, set next action, set owner,
//      add a note, reset the outreach sequence, notify admins or the owner by
//      email and/or Teams). Turning on or saving a timer rule first shows how
//      many leads it would act on straight away. Run log lists the last 200
//      firings; a company name opens the lead. Check timers now runs the
//      15-minute timer check immediately.
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useIsMobile, api, fmtUpdated, money, Modal, ErrorBox, inputStyle, labelStyle, sectionTitle } from "./salesShared";

const TIMER = new Set(["tag_age", "no_response", "next_action_overdue"]);
const TAG_TRIGGERS = new Set(["tag_added", "tag_removed", "tag_age"]);
const CLOSED = ["Won", "Lost", "Dead"];
const NOTIFY_WHO = { admins: "admins", owner: "the lead owner", both: "admins and the lead owner" };

function triggerDefaults(type) {
  switch (type) {
    case "stage_entered": return { type, to: "", from: "" };
    case "tag_added": case "tag_removed": return { type, tag_id: null };
    case "tag_age": return { type, tag_id: null, days: 30 };
    case "no_response": return { type, days: 30 };
    case "next_action_overdue": return { type, days: 3 };
    case "response": return { type, kinds: [] };
    case "lead_created": return { type, source: "" };
    default: return { type };
  }
}

function conditionDefaults(type) {
  switch (type) {
    case "stage_is": case "stage_not": return { type, stages: type === "stage_not" ? [...CLOSED] : [] };
    case "has_tag": case "lacks_tag": return { type, tag_id: null };
    case "no_response_days": return { type, days: 30 };
    case "owner_is": return { type, owner: "__none" };
    case "score_band": return { type, bands: [] };
    case "value_over": return { type, amount: 0 };
    default: return { type };
  }
}

function actionDefaults(type) {
  switch (type) {
    case "move_stage": return { type, stage: "Dead" };
    case "add_tags": case "remove_tags": return { type, tag_ids: [] };
    case "set_next_action": return { type, text: "Follow up", days: 3 };
    case "set_owner": return { type, owner: "" };
    case "add_note": return { type, text: "" };
    case "notify": return { type, to: "admins", email: true, teams: false, message: "" };
    default: return { type };
  }
}

function newRule() {
  return {
    name: "", enabled: true, trigger: triggerDefaults("tag_age"),
    conditions: [{ type: "no_response_since_trigger" }, conditionDefaults("stage_not")],
    actions: [actionDefaults("move_stage"), { type: "remove_trigger_tag" }, actionDefaults("notify")],
  };
}

function tagName(tags, id) {
  const t = (tags || []).find(x => x.id === Number(id));
  return t ? t.name : "a deleted tag";
}

function staffName(staff, u) {
  if (!u) return "nobody";
  const s = (staff || []).find(x => x.username === u);
  return (s?.email || u).split("@")[0];
}

function conditionText(c, tags, staff) {
  switch (c.type) {
    case "stage_is": return `stage is ${(c.stages || []).join(" or ")}`;
    case "stage_not": return `stage is not ${(c.stages || []).join(" or ")}`;
    case "has_tag": return `has ${tagName(tags, c.tag_id)}`;
    case "lacks_tag": return `does not have ${tagName(tags, c.tag_id)}`;
    case "no_response_since_trigger": return "no response since the timer started";
    case "no_response_days": return `no response in the last ${c.days} days`;
    case "owner_is": return c.owner === "__none" ? "has no owner" : `owner is ${staffName(staff, c.owner)}`;
    case "score_band": return `prospect score is ${(c.bands || []).join(" or ")}`;
    case "value_over": return `value over ${money(c.amount)}`;
    case "has_email_contact": return "has a contact with an email address";
    default: return c.type;
  }
}

function actionText(a, tags, staff) {
  switch (a.type) {
    case "move_stage": return `move to ${a.stage}${a.stage === "Lost" && a.lost_reason ? ` (${a.lost_reason})` : ""}`;
    case "revive": return "revive";
    case "add_tags": return `add ${(a.tag_ids || []).map(i => tagName(tags, i)).join(", ")}`;
    case "remove_tags": return `remove ${(a.tag_ids || []).map(i => tagName(tags, i)).join(", ")}`;
    case "remove_trigger_tag": return "remove the triggering tag";
    case "set_next_action": return `next action "${a.text}" in ${a.days} day${Number(a.days) === 1 ? "" : "s"}`;
    case "set_owner": return `owner ${staffName(staff, a.owner)}`;
    case "add_note": return "add a note";
    case "reset_sequence": return "reset the outreach sequence";
    case "notify": {
      const how = [a.email ? "email" : "", a.teams ? "Teams" : ""].filter(Boolean).join(" and ");
      return `notify ${NOTIFY_WHO[a.to] || "admins"} by ${how}`;
    }
    default: return a.type;
  }
}

const panel = { background: "#141414", border: "1px solid #2c2c2e", borderRadius: 10, padding: "12px 14px", minWidth: 0 };
const small = { ...inputStyle, width: "auto", padding: "6px 9px", fontSize: 12 };

function Chips({ options, value, onChange, colorOf }) {
  const v = value || [];
  return (
    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
      {options.map(o => {
        const key = typeof o === "object" ? o.value : o;
        const label = typeof o === "object" ? o.label : o;
        const on = v.includes(key);
        return (
          <button key={key} className="btn btn-secondary btn-sm" aria-pressed={on}
            style={colorOf && !on ? { color: colorOf(key) } : undefined}
            onClick={() => onChange(on ? v.filter(x => x !== key) : [...v, key])}>{label}</button>
        );
      })}
    </div>
  );
}

function Days({ value, min = 1, onChange, suffix = "days" }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <input type="number" min={min} max={365} value={value ?? ""} onChange={e => onChange(e.target.value === "" ? "" : Number(e.target.value))}
        style={{ ...small, width: 72 }} />
      <span style={{ fontSize: 12, color: "#86868b" }}>{suffix}</span>
    </span>
  );
}

function TagSelect({ tags, value, onChange, anyLabel }) {
  return (
    <select value={value || ""} onChange={e => onChange(e.target.value ? Number(e.target.value) : null)} style={small}>
      <option value="">{anyLabel || "Choose a tag…"}</option>
      {(tags || []).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
    </select>
  );
}

function RemoveBtn({ onClick }) {
  return <button className="btn btn-quiet btn-icon btn-sm" style={{ marginLeft: "auto", flexShrink: 0 }} onClick={onClick} aria-label="Remove" title="Remove">×</button>;
}

// ── Editor ────────────────────────────────────────────────────────────────────

function RuleEditor({ initial, catalogue, tags, stages, staff, busy, onCancel, onSave }) {
  const [r, setR] = useState(initial);
  const isTimer = TIMER.has(r.trigger.type);
  const tagTrigger = TAG_TRIGGERS.has(r.trigger.type);
  const setTrig = (patch) => setR(x => ({ ...x, trigger: { ...x.trigger, ...patch } }));
  const setCond = (i, patch) => setR(x => ({ ...x, conditions: x.conditions.map((c, j) => (j === i ? { ...c, ...patch } : c)) }));
  const setAct = (i, patch) => setR(x => ({ ...x, actions: x.actions.map((a, j) => (j === i ? { ...a, ...patch } : a)) }));

  function changeTrigger(type) {
    setR(x => {
      const next = { ...x, trigger: triggerDefaults(type) };
      // A timer that has run out shouldn't pick up closed leads unless asked.
      if (TIMER.has(type) && !x.id && !x.conditions.some(c => c.type === "stage_not" || c.type === "stage_is")) {
        next.conditions = [...x.conditions, conditionDefaults("stage_not")];
      }
      if (!TIMER.has(type)) next.conditions = next.conditions.filter(c => c.type !== "no_response_since_trigger");
      if (!TAG_TRIGGERS.has(type)) next.actions = next.actions.filter(a => a.type !== "remove_trigger_tag");
      return next;
    });
  }

  const stageOpts = stages;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div>
        <label style={labelStyle}>Rule name</label>
        <input value={r.name} maxLength={80} onChange={e => setR(x => ({ ...x, name: e.target.value }))}
          placeholder="e.g. Leaflet Sent: no response in 30 days" style={inputStyle} />
      </div>

      {/* When */}
      <div style={panel}>
        <div style={sectionTitle}>When</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <select value={r.trigger.type} onChange={e => changeTrigger(e.target.value)} style={small}>
            {catalogue.triggers.map(t => <option key={t.type} value={t.type}>{t.label}</option>)}
          </select>
          {r.trigger.type === "stage_entered" && <>
            {/* eslint-disable-next-line react/jsx-key */}
            <select value={r.trigger.to || ""} onChange={e => setTrig({ to: e.target.value })} style={small}>
              <option value="">any stage</option>
              {stageOpts.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            {/* eslint-disable-next-line react/jsx-key */}
            <span style={{ fontSize: 12, color: "#86868b" }}>from</span>
            {/* eslint-disable-next-line react/jsx-key */}
            <select value={r.trigger.from || ""} onChange={e => setTrig({ from: e.target.value })} style={small}>
              <option value="">any stage</option>
              {stageOpts.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </>}
          {(r.trigger.type === "tag_added" || r.trigger.type === "tag_removed") &&
            <TagSelect tags={tags} value={r.trigger.tag_id} onChange={v => setTrig({ tag_id: v })} anyLabel="any tag" />}
          {r.trigger.type === "tag_age" && <>
            {/* eslint-disable-next-line react/jsx-key */}
            <TagSelect tags={tags} value={r.trigger.tag_id} onChange={v => setTrig({ tag_id: v })} />
            {/* eslint-disable-next-line react/jsx-key */}
            <Days value={r.trigger.days} onChange={v => setTrig({ days: v })} />
          </>}
          {r.trigger.type === "no_response" && <Days value={r.trigger.days} onChange={v => setTrig({ days: v })} />}
          {r.trigger.type === "next_action_overdue" && <Days value={r.trigger.days} min={0} onChange={v => setTrig({ days: v })} suffix="days past the date (0 as soon as it is overdue)" />}
          {r.trigger.type === "lead_created" && (
            <select value={r.trigger.source || ""} onChange={e => setTrig({ source: e.target.value })} style={small}>
              <option value="">from anywhere</option>
              <option value="search">from Prospect Search</option>
              <option value="manual">added by hand</option>
            </select>
          )}
        </div>
        {r.trigger.type === "response" && (
          <div style={{ marginTop: 8 }}>
            <div style={{ fontSize: 12, color: "#86868b", marginBottom: 6 }}>Counts as a response (none chosen means any of them):</div>
            <Chips options={catalogue.response_kinds.map(k => ({ value: k.kind, label: k.label }))} value={r.trigger.kinds} onChange={v => setTrig({ kinds: v })} />
          </div>
        )}
        {r.trigger.type === "no_response" && (
          <div style={{ fontSize: 11, color: "#86868b", marginTop: 8 }}>Counted from the later of the last response and when the lead entered its current stage.</div>
        )}
        {isTimer && <div style={{ fontSize: 11, color: "#86868b", marginTop: 8 }}>Timers are checked every {catalogue.sweep_minutes} minutes. A rule acts once on a lead each time its timer runs out.</div>}
      </div>

      {/* If */}
      <div style={panel}>
        <div style={sectionTitle}>If (all of these)</div>
        {r.conditions.length === 0 && <div style={{ fontSize: 12, color: "#86868b", marginBottom: 8 }}>No conditions: the rule acts on every lead the trigger matches.</div>}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {r.conditions.map((c, i) => (
            <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", borderBottom: "1px solid #232325", paddingBottom: 8 }}>
              <select value={c.type} onChange={e => setR(x => ({ ...x, conditions: x.conditions.map((cc, j) => (j === i ? conditionDefaults(e.target.value) : cc)) }))} style={small}>
                {catalogue.conditions.map(o => (
                  <option key={o.type} value={o.type} disabled={o.type === "no_response_since_trigger" && !isTimer}>{o.label}</option>
                ))}
              </select>
              {(c.type === "stage_is" || c.type === "stage_not") && <Chips options={stageOpts} value={c.stages} onChange={v => setCond(i, { stages: v })} />}
              {(c.type === "has_tag" || c.type === "lacks_tag") && <TagSelect tags={tags} value={c.tag_id} onChange={v => setCond(i, { tag_id: v })} />}
              {c.type === "no_response_days" && <Days value={c.days} onChange={v => setCond(i, { days: v })} />}
              {c.type === "owner_is" && (
                <select value={c.owner} onChange={e => setCond(i, { owner: e.target.value })} style={small}>
                  <option value="__none">No owner</option>
                  {staff.map(s => <option key={s.username} value={s.username}>{s.email}</option>)}
                </select>
              )}
              {c.type === "score_band" && <Chips options={catalogue.score_bands} value={c.bands} onChange={v => setCond(i, { bands: v })} />}
              {c.type === "value_over" && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontSize: 12, color: "#86868b" }}>£</span>
                  <input type="number" min="0" step="50" value={c.amount} onChange={e => setCond(i, { amount: e.target.value })} style={{ ...small, width: 100 }} />
                </span>
              )}
              <RemoveBtn onClick={() => setR(x => ({ ...x, conditions: x.conditions.filter((_, j) => j !== i) }))} />
            </div>
          ))}
        </div>
        <button className="btn btn-secondary btn-sm" style={{ marginTop: 8 }} disabled={r.conditions.length >= 12}
          onClick={() => setR(x => ({ ...x, conditions: [...x.conditions, conditionDefaults("stage_is")] }))}>+ Condition</button>
      </div>

      {/* Then */}
      <div style={panel}>
        <div style={sectionTitle}>Then</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {r.actions.map((a, i) => (
            <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", borderBottom: "1px solid #232325", paddingBottom: 8 }}>
              <select value={a.type} onChange={e => setR(x => ({ ...x, actions: x.actions.map((aa, j) => (j === i ? actionDefaults(e.target.value) : aa)) }))} style={small}>
                {catalogue.actions.map(o => (
                  <option key={o.type} value={o.type} disabled={o.type === "remove_trigger_tag" && !(tagTrigger && r.trigger.tag_id)}>{o.label}</option>
                ))}
              </select>
              {a.type === "move_stage" && (
                <select value={a.stage} onChange={e => setAct(i, { stage: e.target.value, lost_reason: e.target.value === "Lost" ? (a.lost_reason || "No response") : undefined })} style={small}>
                  {stageOpts.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              )}
              {a.type === "move_stage" && a.stage === "Lost" && (
                <input value={a.lost_reason || ""} maxLength={300} onChange={e => setAct(i, { lost_reason: e.target.value })} placeholder="Lost reason" style={{ ...small, flex: "1 1 160px" }} />
              )}
              {(a.type === "add_tags" || a.type === "remove_tags") && (
                <Chips options={tags.map(t => ({ value: t.id, label: t.name }))} value={a.tag_ids} onChange={v => setAct(i, { tag_ids: v })}
                  colorOf={id => (tags.find(t => t.id === id) || {}).color} />
              )}
              {a.type === "set_next_action" && <>
                {/* eslint-disable-next-line react/jsx-key */}
                <input value={a.text} maxLength={300} onChange={e => setAct(i, { text: e.target.value })} placeholder="e.g. Call the director" style={{ ...small, flex: "1 1 160px" }} />
                {/* eslint-disable-next-line react/jsx-key */}
                <Days value={a.days} min={0} onChange={v => setAct(i, { days: v })} suffix="days from then" />
              </>}
              {a.type === "set_owner" && (
                <select value={a.owner} onChange={e => setAct(i, { owner: e.target.value })} style={small}>
                  <option value="">No owner</option>
                  {staff.map(s => <option key={s.username} value={s.username}>{s.email}</option>)}
                </select>
              )}
              {a.type === "add_note" && (
                <input value={a.text} maxLength={2000} onChange={e => setAct(i, { text: e.target.value })} placeholder="Note text" style={{ ...small, flex: "1 1 200px" }} />
              )}
              {a.type === "notify" && <>
                {/* eslint-disable-next-line react/jsx-key */}
                <select value={a.to} onChange={e => setAct(i, { to: e.target.value })} style={small}>
                  <option value="admins">Admins</option>
                  <option value="owner">Lead owner</option>
                  <option value="both">Admins and owner</option>
                </select>
                {/* eslint-disable-next-line react/jsx-key */}
                <label style={{ display: "inline-flex", gap: 5, alignItems: "center", fontSize: 12, color: "#d1d1d6" }}>
                  <input type="checkbox" checked={!!a.email} onChange={e => setAct(i, { email: e.target.checked })} style={{ accentColor: "#00d4ff" }} /> Email
                </label>
                {/* eslint-disable-next-line react/jsx-key */}
                <label style={{ display: "inline-flex", gap: 5, alignItems: "center", fontSize: 12, color: "#d1d1d6" }}>
                  <input type="checkbox" checked={!!a.teams} onChange={e => setAct(i, { teams: e.target.checked })} style={{ accentColor: "#00d4ff" }} /> Teams
                </label>
                {/* eslint-disable-next-line react/jsx-key */}
                <input value={a.message || ""} maxLength={500} onChange={e => setAct(i, { message: e.target.value })} placeholder="Message (optional; otherwise it says what the rule did)" style={{ ...small, flex: "1 1 100%" }} />
              </>}
              <RemoveBtn onClick={() => setR(x => ({ ...x, actions: x.actions.filter((_, j) => j !== i) }))} />
            </div>
          ))}
        </div>
        <button className="btn btn-secondary btn-sm" style={{ marginTop: 8 }} disabled={r.actions.length >= 12}
          onClick={() => setR(x => ({ ...x, actions: [...x.actions, actionDefaults("add_tags")] }))}>+ Action</button>
        <div style={{ fontSize: 11, color: "#86868b", marginTop: 8 }}>Notifications are sent after the other actions, and one email or Teams card covers every lead the rule acted on in the same check.</div>
      </div>

      <label style={{ display: "inline-flex", gap: 8, alignItems: "center", fontSize: 13, color: "#f5f5f7" }}>
        <input type="checkbox" checked={r.enabled} onChange={e => setR(x => ({ ...x, enabled: e.target.checked }))} style={{ accentColor: "#00d4ff" }} />
        Rule is on
      </label>

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
        <button className="btn btn-secondary" onClick={onCancel}>Cancel</button>
        <button className="btn btn-primary" disabled={busy || !r.name.trim() || r.actions.length === 0} onClick={() => onSave(r)}>{busy ? "Saving…" : "Save rule"}</button>
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────

export default function SalesRules({ tags, meta, onClose }) {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [view, setView] = useState("list");   // list | edit | log
  const [draft, setDraft] = useState(null);
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);  // { rule, preview, mode }
  const stages = meta?.stages || ["New", "Researching", "Contacted", "Engaged", "Proposal", "Won", "Lost", "Dead"];
  const staff = meta?.staff || [];

  useEffect(() => { api("/api/sales/rules").then(setData).catch(e => setErr(e.message)); }, []);

  async function act(fn, after) {
    setBusy(true); setErr(""); setInfo("");
    try { const d = await fn(); if (d && d.rules) setData(d); if (after) after(d); return d; }
    catch (e) { setErr(e.message); return null; }
    finally { setBusy(false); }
  }

  const persist = (rule) => act(
    () => (rule.id
      ? api(`/api/sales/rules/${rule.id}`, { method: "PUT", body: rule })
      : api("/api/sales/rules", { method: "POST", body: rule })),
    () => { setView("list"); setDraft(null); setConfirm(null); setInfo(`Saved ${rule.name}.`); });

  const setEnabled = (rule, enabled) => act(
    () => api(`/api/sales/rules/${rule.id}`, { method: "PUT", body: { enabled } }),
    () => { setConfirm(null); setInfo(`${rule.name} is ${enabled ? "on" : "off"}.`); });

  // Timer rules being switched on show how many leads they would act on now.
  async function checkFirst(rule, mode) {
    if (!(rule.enabled || mode === "toggle") || !TIMER.has(rule.trigger.type)) {
      return mode === "toggle" ? setEnabled(rule, true) : persist(rule);
    }
    const p = await act(() => api("/api/sales/rules/preview", { method: "POST", body: { ...rule, enabled: true } }));
    if (!p) return;
    if (p.count === 0) return mode === "toggle" ? setEnabled(rule, true) : persist(rule);
    setConfirm({ rule, preview: p, mode });
  }

  async function del(rule) {
    if (!window.confirm(`Delete the rule "${rule.name}"?`)) return;
    act(() => api(`/api/sales/rules/${rule.id}`, { method: "DELETE" }), () => setInfo(`Deleted ${rule.name}.`));
  }

  async function runTimers() {
    const d = await act(() => api("/api/sales/rules/run-timers", { method: "POST", body: {} }));
    if (d) setInfo(d.fired ? `Timer rules acted on ${d.fired} lead${d.fired === 1 ? "" : "s"}. See the run log.` : "No timer rules were due.");
  }

  const openLead = (id) => { onClose(); navigate(`/sales/pipeline?lead=${id}`); };

  const title = view === "edit" ? (draft?.id ? `Edit rule` : "New rule") : "Pipeline rules";
  return (
    <Modal title={title} onClose={onClose} maxWidth={860}>
      <ErrorBox>{err}</ErrorBox>
      {info && <div style={{ background: "rgba(0,212,255,0.08)", border: "1px solid rgba(0,212,255,0.25)", borderRadius: 8, padding: "9px 12px", color: "#64d2ff", fontSize: 13 }}>{info}</div>}
      {!data ? <span className="spinner" /> : confirm ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ fontSize: 14, color: "#f5f5f7" }}>
            Turning on <b>{confirm.rule.name}</b> would act on {confirm.preview.count} lead{confirm.preview.count === 1 ? "" : "s"} at the next check:
          </div>
          <div style={{ ...panel, display: "flex", flexDirection: "column", gap: 4, maxHeight: 260, overflow: "auto" }}>
            {confirm.preview.leads.map(l => (
              <div key={l.id} style={{ fontSize: 13, color: "#d1d1d6" }}>{l.company_name} <span style={{ color: "#86868b" }}>· {l.stage}</span></div>
            ))}
            {confirm.preview.count > confirm.preview.leads.length && <div style={{ fontSize: 12, color: "#86868b" }}>and {confirm.preview.count - confirm.preview.leads.length} more</div>}
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
            <button className="btn btn-secondary" onClick={() => setConfirm(null)}>Back</button>
            {confirm.mode === "save" && <button className="btn btn-secondary" disabled={busy} onClick={() => persist({ ...confirm.rule, enabled: false })}>Save switched off</button>}
            <button className="btn btn-primary" disabled={busy} onClick={() => (confirm.mode === "toggle" ? setEnabled(confirm.rule, true) : persist(confirm.rule))}>
              {confirm.mode === "toggle" ? "Turn it on" : "Save and turn on"}
            </button>
          </div>
        </div>
      ) : view === "edit" ? (
        <RuleEditor initial={draft} catalogue={data} tags={tags} stages={stages} staff={staff} busy={busy}
          onCancel={() => { setView("list"); setDraft(null); }} onSave={r => checkFirst(r, "save")} />
      ) : (
        <>
          {/* eslint-disable-next-line react/jsx-key */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <button className="btn btn-secondary btn-sm" aria-pressed={view === "list"} onClick={() => setView("list")}>Rules ({data.rules.length})</button>
            <button className="btn btn-secondary btn-sm" aria-pressed={view === "log"} onClick={() => setView("log")}>Run log</button>
            <span style={{ flex: 1 }} />
            <button className="btn btn-secondary btn-sm" disabled={busy} onClick={runTimers} title={`Timer rules are checked every ${data.sweep_minutes} minutes anyway`}>Check timers now</button>
            <button className="btn btn-primary btn-sm" onClick={() => { setDraft(newRule()); setView("edit"); setInfo(""); }}>+ New rule</button>
          </div>

          {/* eslint-disable-next-line react/jsx-key */}
          {view === "list" ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {data.rules.length === 0 && <div style={{ fontSize: 13, color: "#86868b" }}>No rules yet.</div>}
              {data.rules.map(rule => (
                <div key={rule.id} style={{ ...panel, opacity: rule.enabled ? 1 : 0.7, display: "flex", flexDirection: "column", gap: 6 }}>
                  <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                    <label style={{ display: "inline-flex", gap: 6, alignItems: "center", fontSize: 12, color: rule.enabled ? "#30d158" : "#86868b", cursor: "pointer" }}>
                      <input type="checkbox" checked={rule.enabled} disabled={busy}
                        onChange={e => (e.target.checked ? checkFirst(rule, "toggle") : setEnabled(rule, false))} style={{ accentColor: "#30d158" }} />
                      {rule.enabled ? "On" : "Off"}
                    </label>
                    <span style={{ fontSize: 14, fontWeight: 700, color: "#f5f5f7", flex: "1 1 200px", minWidth: 0, overflowWrap: "anywhere" }}>{rule.name}</span>
                    {rule.timer && <span style={{ fontSize: 10, fontWeight: 700, color: "#ff9f0a", border: "1px solid rgba(255,159,10,0.4)", borderRadius: 5, padding: "1px 6px" }}>TIMER</span>}
                    <button className="btn btn-secondary btn-sm" onClick={() => { setDraft(JSON.parse(JSON.stringify(rule))); setView("edit"); setInfo(""); }}>Edit</button>
                    <button className="btn btn-quiet btn-icon btn-sm" title="Delete rule" onClick={() => del(rule)}><i className="ti ti-trash" /></button>
                  </div>
                  {rule.problem && <div style={{ fontSize: 12, color: "#ff453a" }}>Needs attention: {rule.problem}.</div>}
                  <div style={{ fontSize: 12, color: "#d1d1d6", lineHeight: 1.6 }}>
                    <b style={{ color: "#86868b" }}>When</b> {rule.trigger_text}
                    {rule.conditions.length > 0 && <><br /><b style={{ color: "#86868b" }}>If</b> {rule.conditions.map(c => conditionText(c, tags, staff)).join(", and ")}</>}
                    <br /><b style={{ color: "#86868b" }}>Then</b> {rule.actions.map(a => actionText(a, tags, staff)).join(", then ")}
                  </div>
                  <div style={{ fontSize: 11, color: "#636366" }}>
                    {rule.fire_count ? `Ran ${rule.fire_count} time${rule.fire_count === 1 ? "" : "s"}, last ${fmtUpdated(rule.last_fired_at)}` : "Has not run yet"}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {data.log.length === 0 && <div style={{ fontSize: 13, color: "#86868b" }}>No rule has run yet.</div>}
              {data.log.map(x => (
                <div key={x.id} style={{ display: "flex", gap: isMobile ? 4 : 12, flexDirection: isMobile ? "column" : "row", padding: "8px 2px", borderBottom: "1px solid #232325", minWidth: 0 }}>
                  <span style={{ fontSize: 11, color: "#86868b", width: isMobile ? "auto" : 120, flexShrink: 0 }}>{fmtUpdated(x.created_at)}</span>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 13, color: "#f5f5f7", overflowWrap: "anywhere" }}>
                      {x.lead_id ? <button className="btn-bare" style={{ color: "#64d2ff", fontWeight: 600, padding: 0 }} onClick={() => openLead(x.lead_id)}>{x.company_name}</button> : x.company_name}
                      <span style={{ color: "#86868b" }}> · {x.rule_name}</span>
                    </div>
                    <div style={{ fontSize: 12, color: "#d1d1d6", overflowWrap: "anywhere" }}>{x.trigger_text}: {x.actions_text}</div>
                    {x.error && <div style={{ fontSize: 12, color: "#ff453a" }}>Failed: {x.error}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </Modal>
  );
}
